import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, fromEvent, map, Observable } from 'rxjs';

enum TabManagementMessageType {
  TabActivated = 'TAB_ACTIVATED',
  TabHeartbeat = 'TAB_HEARTBEAT',
  TabClosed = 'TAB_CLOSED',
  RequestActiveTab = 'REQUEST_ACTIVE_TAB',
  ActiveTabResponse = 'ACTIVE_TAB_RESPONSE',
}

interface TabManagementMessage {
  type: TabManagementMessageType;
  tabId: string;
  timestamp: number;
  targetTabId?: string;
}

interface TabInfo {
  id: string;
  lastSeen: number;
  isActive: boolean;
}

interface TabData<T extends object> {
  type: string;
  tabId: string;
  payload: T;
}

const ELECTION_DELAY = 150;
const TAB_TIMEOUT = 5000;
const HEARTBEAT_INTERVAL = 2000;

@Injectable({ providedIn: 'root' })
export class TabManagementService {
  readonly tabId = crypto.randomUUID();

  readonly activeTabId$ = new BehaviorSubject<string | null>(null);

  readonly isActive$ = this.activeTabId$.pipe(map((activeTabId) => activeTabId === this.tabId));

  get isActive(): boolean {
    return this.activeTabId$.value === this.tabId;
  }

  private readonly managementChannel = new BroadcastChannel('tab_management');

  private readonly dataChannel = new BroadcastChannel('tab_data');

  private readonly tabs = new Map<string, TabInfo>();

  public init(): void {
    this.managementChannel.addEventListener('message', (event: MessageEvent<TabManagementMessage>) => {
      const { type, tabId, timestamp, targetTabId } = event.data;

      if (targetTabId && targetTabId !== this.tabId) {
        return;
      }

      this.updateTabInfo(tabId, timestamp);

      switch (type) {
        case TabManagementMessageType.TabActivated:
          this.handleTabActivated(tabId, timestamp);
          break;
        case TabManagementMessageType.TabHeartbeat:
          this.handleHeartbeat(tabId, timestamp);
          break;
        case TabManagementMessageType.TabClosed:
          this.handleTabClosed(tabId);
          break;
        case TabManagementMessageType.RequestActiveTab:
          this.handleActiveTabRequest(tabId);
          break;
        case TabManagementMessageType.ActiveTabResponse:
          this.handleActiveTabResponse(tabId);
          break;
      }
    });

    this.startHeartbeat();
    this.setupFocusTracking();
    this.setupCleanupInterval();

    this.requestActiveTab();

    window.addEventListener('unload', () => {
      this.broadcast(TabManagementMessageType.TabClosed);
      this.destroy();
    });
  }

  public listenData<T extends object>(type: string): Observable<T> {
    return fromEvent<MessageEvent<TabData<T>>>(this.dataChannel, 'message').pipe(
      filter((event) => event.data.type === type && event.data.tabId !== this.tabId),
      map((event) => event.data.payload),
    );
  }

  public broadcastData<T extends object>(type: string, payload: T): void {
    const message: TabData<T> = {
      tabId: this.tabId,
      type,
      payload,
    };

    this.dataChannel.postMessage(message);
  }

  protected destroy(): void {
    this.managementChannel.close();
  }

  protected updateTabInfo(tabId: string, timestamp: number, isActive?: boolean): void {
    const tab = this.tabs.get(tabId);

    if (tab) {
      tab.lastSeen = timestamp;

      if (typeof isActive === 'boolean') {
        tab.isActive = isActive;
      }
    } else {
      this.tabs.set(tabId, {
        id: tabId,
        lastSeen: timestamp,
        isActive: isActive ?? false,
      });
    }
  }

  protected handleTabActivated(tabId: string, timestamp: number): void {
    if (tabId !== this.tabId) {
      this.activeTabId$.next(tabId);
      this.updateTabInfo(tabId, timestamp, true);
    }
  }

  protected handleHeartbeat(tabId: string, timestamp: number): void {
    if (this.activeTabId$.value === tabId) {
      this.updateTabInfo(tabId, timestamp);
    }
  }

  protected handleTabClosed(tabId: string): void {
    this.tabs.delete(tabId);

    if (this.activeTabId$.value === tabId) {
      setTimeout(() => this.electNewActiveTab(), ELECTION_DELAY);
    }
  }

  protected handleActiveTabRequest(requestorId: string): void {
    if (this.isActive) {
      this.sendToTab(requestorId, TabManagementMessageType.ActiveTabResponse);
    }
  }

  protected handleActiveTabResponse(tabId: string): void {
    if (!this.activeTabId$.value) {
      this.activeTabId$.next(tabId);
    }
  }

  protected requestActiveTab(): void {
    this.broadcast(TabManagementMessageType.RequestActiveTab);

    setTimeout(() => {
      if (!this.activeTabId$.value) {
        this.activateThisTab();
      }
    }, ELECTION_DELAY);
  }

  protected activateThisTab() {
    if (this.isActive) {
      return;
    }

    this.activeTabId$.next(this.tabId);
    this.broadcast(TabManagementMessageType.TabActivated);
  }

  protected electNewActiveTab(): void {
    const now = Date.now();
    let oldestTab: string | null = null;
    let oldestTime = Infinity;

    for (const [id, tab] of this.tabs) {
      if (now - tab.lastSeen < TAB_TIMEOUT) {
        if (tab.lastSeen < oldestTime) {
          oldestTab = id;
          oldestTime = tab.lastSeen;
        }
      }
    }

    if (oldestTab) {
      this.sendMessage({
        tabId: oldestTab,
        type: TabManagementMessageType.TabActivated,
        timestamp: Date.now(),
        targetTabId: oldestTab,
      });
    } else if (!this.isActive) {
      this.activateThisTab();
    }
  }

  protected startHeartbeat(): void {
    setInterval(() => {
      if (this.isActive) {
        this.broadcast(TabManagementMessageType.TabHeartbeat);
      }
    }, HEARTBEAT_INTERVAL);
  }

  protected setupFocusTracking(): void {
    const activateHandler = () => {
      if (document.visibilityState === 'visible') {
        this.activateThisTab();
      }
    };

    document.addEventListener('visibilitychange', activateHandler);
    document.addEventListener('focus', activateHandler);
  }

  protected setupCleanupInterval(): void {
    setInterval(() => {
      const now = Date.now();
      for (const [id, tab] of this.tabs) {
        if (now - tab.lastSeen > TAB_TIMEOUT * 2) {
          this.tabs.delete(id);
        }
      }
    }, 6000);
  }

  protected broadcast(type: TabManagementMessageType): void {
    this.sendMessage({
      tabId: this.tabId,
      timestamp: Date.now(),
      type,
    });
  }

  protected sendToTab(targetTabId: string, type: TabManagementMessageType): void {
    this.sendMessage({
      tabId: this.tabId,
      timestamp: Date.now(),
      type,
      targetTabId,
    });
  }

  protected sendMessage(message: TabManagementMessage): void {
    this.managementChannel.postMessage(message);
  }
}
